import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5179/Api';

let getAppState: () => any = () => ({ accessToken: '', refreshToken: '' });
let setAppState: (updates: any) => void = () => {};

export const setApiAppStateBridge = ({ getState, updateState }: { getState?: () => any; updateState?: (updates: any) => void }) => {
  if (typeof getState === 'function') {
    getAppState = getState;
  }
  if (typeof updateState === 'function') {
    setAppState = updateState;
  }
};

export const extractErrorMessage = (data: any, status?: number) => {
  if (typeof status === 'number' && status >= 500) {
    return 'Something went wrong. Please try again later.';
  }

  if (!data) {
    return 'An error occurred';
  }

  if (typeof data === 'string') {
    return data;
  }

  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors.join(' | ');
  }

  if (typeof data?.message === 'string' && data.message.trim() !== '') {
    return data.message;
  }

  return 'An error occurred';
};

const instance = axios.create({ baseURL: BASE_URL });

let refreshTokenPromise: Promise<string> | null = null;
const activeRequests = new Set<string>();

const addRequest = (requestId: string) => {
  activeRequests.add(requestId);
  setAppState({ requestSet: Array.from(activeRequests), loading: true });
};

const removeRequest = (requestId: string) => {
  activeRequests.delete(requestId);
  setAppState({
    requestSet: Array.from(activeRequests),
    loading: activeRequests.size > 0,
  });
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (activeRequests.size > 0) {
      activeRequests.clear();
      setAppState({ requestSet: [], loading: false });
    }
  });
}

instance.interceptors.request.use(
  (config) => {
    const appState = getAppState();

    config.headers = config.headers || {};
    if (appState?.accessToken) {
      (config.headers as any).Authorization = `Bearer ${appState.accessToken}`;
    }

    const requestId = uuidv4();
    (config.headers as any)['X-Request-ID'] = requestId;
    addRequest(requestId);
    return config;
  },
  (error) => {
    const requestId = error.config?.headers?.['X-Request-ID'];
    if (requestId) {
      removeRequest(requestId);
    }
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response) => {
    const requestId = response.config?.headers?.['X-Request-ID'];
    if (requestId) {
      removeRequest(requestId);
    }
    return response;
  },
  async (error) => {
    const requestId = error.config?.headers?.['X-Request-ID'];
    if (requestId) {
      removeRequest(requestId);
    }

    const appState = getAppState();
    const requestUrl: string = error.config?.url || '';
    const isRefreshRequest = requestUrl.includes('/users/refresh-token');

    if (error.response?.status === 401 && appState?.refreshToken && !isRefreshRequest) {
      try {
        if (!refreshTokenPromise) {
          refreshTokenPromise = instance
            .post('/users/refresh-token', {
              accessToken: appState.accessToken,
              refreshToken: appState.refreshToken,
            })
            .then((response) => {
              const newAccessToken = response.data.AccessToken;
              setAppState({ accessToken: newAccessToken, isAuthenticated: true });
              return newAccessToken;
            })
            .finally(() => {
              refreshTokenPromise = null;
            });
        }

        const newAccessToken = await refreshTokenPromise;
        error.config.headers = error.config.headers || {};
        error.config.headers.Authorization = `Bearer ${newAccessToken}`;
        return instance(error.config);
      } catch (refreshError) {
        setAppState({
          error: 'Session expired. Please log in again.',
          accessToken: '',
          refreshToken: '',
          isAuthenticated: false,
          user: null,
        });
        return Promise.reject(refreshError);
      }
    }

    if (error.response?.status == null || error.response?.status === 401) {
      setAppState({
        isAuthenticated: false,
      });
      return Promise.reject(error);
    }

    setAppState({
      error: extractErrorMessage(error.response?.data, error.response?.status),
    });
    return Promise.reject(error);
  }
);

export const createApiInstance = () => instance;

// basic axios api without params and interceptors
export const Api = axios.create({ baseURL: BASE_URL });
